import { useMutation, useQueryClient } from '@tanstack/react-query';
import { attachProductsToGroup } from '../services/security-groups';

export function useAttachSecurityGroupsMutation(productId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (securityGroupIds: string[]) => {
      const results = await Promise.allSettled(
        securityGroupIds.map((sgId) => attachProductsToGroup(sgId, [productId])),
      );
      const failures = results
        .map((r, i) => ({ r, sgId: securityGroupIds[i] }))
        .filter(({ r }) => r.status === 'rejected');
      if (failures.length > 0) {
        // out_of_scope_ids from POST /security/groups/{id}/products contains
        // product IDs (the attachment entities), not security-group IDs.
        // We check whether the fixed productId appears in the rejection payload;
        // if it does, the SG that rejected it is out of scope.
        const outOfScope = failures.flatMap(({ sgId, r }) => {
          const data = (r as PromiseRejectedResult).reason?.response?.data;
          return data?.out_of_scope_ids?.includes(productId) ? [sgId] : [];
        });
        throw Object.assign(new Error('Partial failure'), {
          response: { data: { out_of_scope_ids: outOfScope } },
        });
      }
      return { attached: securityGroupIds.length };
    },
    // Use onSettled so cache invalidations run even on partial success (i.e.
    // when mutationFn throws after some per-SG requests succeeded).
    onSettled: (_data, _error, securityGroupIds) => {
      qc.invalidateQueries({ queryKey: ['security', 'products', productId, 'groups'] });
      qc.invalidateQueries({ queryKey: ['security-groups'] });
      securityGroupIds.forEach((sgId) => {
        qc.invalidateQueries({ queryKey: ['security-group', sgId, 'products'] });
        qc.invalidateQueries({ queryKey: ['security-group', sgId] });
      });
    },
  });
}
